<script setup lang="ts">
import { BaseSerializedChart, SerializedRegionChart } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { SerializedGeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { computed, ComputedRef, onMounted, Ref, ref, watch } from 'vue'
import { useRegionStore } from '@/store/regions'
import CameraTagSelector from '@/components/charts/chart-creator/CameraTagSelector.vue'
import { TagSelector, xAggregations } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import XAggregationPicker from '@/components/charts/chart-creator/XAggregationPicker.vue'
import LabelTagSelector from '@/components/charts/chart-creator/LabelTagSelector.vue'
import MultiTagSelector from '@/components/charts/chart-creator/MultiTagSelector.vue'
import _ from 'lodash'
import { useDisplay } from 'vuetify'

const props = defineProps<{ baseSerialized: BaseSerializedChart | null; timeSetup: SerializedGeneralChartTimeSetup | null }>()
const model = defineModel<SerializedRegionChart | null>()

const { mobile } = useDisplay()

const availableRegionTypes: Ref<string[]> = ref([])
const regionSpecs: Ref<{ regionType: string; regionSpecificationId: string }[]> = ref([])
const pickedRegionType: Ref<string> = ref('')
const useAllRegionSpecs: Ref<boolean> = ref(true)
const pickedRegionSpecs: Ref<string[]> = ref([])
const groupByRegionSpec: Ref<boolean> = ref(true)
const allowEmptyRegionSpecs: Ref<boolean> = ref(false)
const xAggregation: Ref<xAggregations> = ref('mean')
const tagSelectors: Ref<TagSelector<string>[]> = ref([])
const cameraTagSelector: Ref<TagSelector<'camera'>> = ref({
  useAll: true,
  groupByValues: false,
  tag: 'camera',
  allowEmptyValues: true,
  specificValues: [],
})
const labelTagSelector: Ref<TagSelector<'label'>> = ref({
  useAll: true,
  groupByValues: false,
  tag: 'label',
  allowEmptyValues: false,
  specificValues: [],
})

const availableRegionSpecs: ComputedRef<string[]> = computed(() => {
  if (!pickedRegionType.value) return []
  return Object.values(regionSpecs.value)
    .filter((regionSpec: { regionType: string; regionSpecificationId: string }) => regionSpec.regionType === pickedRegionType.value)
    .map(regionSpec => regionSpec.regionSpecificationId)
})

const compiledRegionSpecTagSelector: ComputedRef<TagSelector<string>> = computed(() => {
  return {
    tag: pickedRegionType.value,
    useAll: useAllRegionSpecs.value,
    groupByValues: groupByRegionSpec.value,
    ...(!useAllRegionSpecs.value && pickedRegionSpecs.value.length && { specificValues: pickedRegionSpecs.value }),
    allowEmptyValues: allowEmptyRegionSpecs.value,
  }
})

const compiledModel: ComputedRef<SerializedRegionChart | null> = computed(() => {
  if (!props.baseSerialized) return null
  if (!props.timeSetup) return null
  return {
    ...props.baseSerialized,
    xAggregation: xAggregation.value,
    regionType: pickedRegionType.value,
    regionSpecTagSelector: compiledRegionSpecTagSelector.value,
    cameraTagSelector: cameraTagSelector.value,
    labelTagSelector: labelTagSelector.value,
    timeSetup: props.timeSetup,
    chartType: CHART_TYPES.REGIONS,
    tagSelectors: tagSelectors.value,
  }
})

async function load() {
  const regionSetup = await useRegionStore().loadRegionSetup()
  availableRegionTypes.value = Object.keys(regionSetup.regionTypes || {})
  regionSpecs.value = regionSetup?.regionSpecifications || []
  if (!availableRegionTypes.value.includes(pickedRegionType.value)) {
    pickedRegionType.value = availableRegionTypes.value[0]
  }
}

onMounted(async () => {
  await load()
})

watch(
  model,
  () => {
    if (!model.value) return
    if (_.isEqual(model.value, compiledModel.value)) return
    pickedRegionType.value = model.value.regionType
    xAggregation.value = model.value.xAggregation
    cameraTagSelector.value = model.value.cameraTagSelector || {
      useAll: true,
      groupByValues: false,
      tag: 'camera',
      allowEmptyValues: true,
      specificValues: [],
    }
    labelTagSelector.value = model.value.labelTagSelector || {
      useAll: true,
      groupByValues: false,
      tag: 'label',
      allowEmptyValues: false,
      specificValues: [],
    }
    tagSelectors.value = model.value.tagSelectors || []
    const regionSpecTagSelector = model.value.regionSpecTagSelector
    if (regionSpecTagSelector) {
      useAllRegionSpecs.value = regionSpecTagSelector.useAll || false
      groupByRegionSpec.value = regionSpecTagSelector.groupByValues || false
      allowEmptyRegionSpecs.value = regionSpecTagSelector.allowEmptyValues || false
      if (!regionSpecTagSelector.useAll && regionSpecTagSelector.specificValues) {
        pickedRegionSpecs.value = regionSpecTagSelector.specificValues
      }
    }
  },
  { immediate: true, deep: true }
)

watch(
  compiledModel,
  () => {
    if (_.isEqual(model.value, compiledModel.value)) return
    model.value = compiledModel.value
  },
  { immediate: true, deep: true }
)
</script>

<template>
  <v-layout-card
    class="pa-0"
    variant="flat"
    elevation="0"
  >
    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-vector-square-edit"
        start
      />
      {{ $t('chart_sharing.creator.customize.region_settings') }}
    </v-card-title>
    <v-card-text
      class="pb-0 pt-2"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <div>
        <XAggregationPicker v-model="xAggregation" />

        <v-autocomplete
          v-model="pickedRegionType"
          prepend-icon="mdi-selection-marker"
          :items="availableRegionTypes"
          variant="outlined"
          :label="$t('chart_sharing.creator.customize.tag_selectors.regions.type')"
        />

        <v-switch
          v-model="groupByRegionSpec"
          :label="$t('chart_sharing.creator.customize.tag_selectors.regions.group_by')"
          hide-details
        />
        <v-switch
          v-model="allowEmptyRegionSpecs"
          :label="$t('chart_sharing.creator.customize.tag_selectors.regions.allow_empty')"
          hide-details
        />
        <v-switch
          v-model="useAllRegionSpecs"
          :label="$t('chart_sharing.creator.customize.tag_selectors.regions.use_all')"
          hide-details
        />
        <v-fade-transition>
          <v-autocomplete
            v-if="!useAllRegionSpecs"
            v-model="pickedRegionSpecs"
            prepend-icon="mdi-vector-intersection"
            :label="$t('chart_sharing.creator.customize.tag_selectors.regions.specific')"
            :disabled="useAllRegionSpecs"
            :items="availableRegionSpecs"
            :multiple="true"
            variant="outlined"
            chips
            closable-chips
            single-line
          />
        </v-fade-transition>
      </div>
    </v-card-text>

    <v-divider
      class="my-8"
      thickness="2"
    />

    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-cctv"
        start
      />
      {{ $t('chart_sharing.creator.customize.tag_selectors.camera.title') }}
    </v-card-title>
    <v-card-text
      class="py-0"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <CameraTagSelector v-model="cameraTagSelector" />
    </v-card-text>

    <v-divider
      class="my-8"
      thickness="2"
    />

    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-tag-multiple"
        start
      />
      {{ $t('chart_sharing.creator.customize.tag_selectors.label.title') }}
    </v-card-title>
    <v-card-text
      class="py-0"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <LabelTagSelector v-model="labelTagSelector" />
    </v-card-text>

    <v-divider
      class="my-8"
      thickness="2"
    />

    <MultiTagSelector
      v-model="tagSelectors"
      measurement="regions"
    />
  </v-layout-card>
</template>
